/**
 * HTTP client.
 *
 * The **only** place in the application that calls `fetch`. An ESLint rule bans
 * the global everywhere else, so a component cannot quietly acquire a network
 * dependency and break the offline story in Phase 5.
 *
 * Responsibilities: authentication headers, request correlation, and translating
 * the backend's error contract into typed errors. No business logic, no caching,
 * no retries — retries belong to the outbox, which has the business semantics to
 * do them safely.
 */

export interface ApiErrorBody {
  type: string;
  title: string;
  status: number;
  detail: string;
  errors?: Record<string, string[]>;
  extra?: Record<string, unknown>;
  request_id?: string;
}

/**
 * A failure the server described.
 *
 * `code` is the machine-readable discriminator — `INSUFFICIENT_STOCK`,
 * `SHIFT_NOT_OPEN`, `VERSION_CONFLICT`. The interface branches on that, never on
 * the English sentence, because the sentence will be translated into isiZulu and
 * Sesotho and the branch must keep working.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly detail: string;
  readonly fieldErrors: Record<string, string[]>;
  readonly extra: Record<string, unknown>;
  readonly requestId: string | undefined;

  constructor(status: number, body: Partial<ApiErrorBody>, requestId?: string) {
    super(body.detail ?? body.title ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = body.type ?? "UNKNOWN";
    this.detail = body.detail ?? "";
    this.fieldErrors = body.errors ?? {};
    this.extra = body.extra ?? {};
    this.requestId = body.request_id ?? requestId;
  }

  /** True when retrying unchanged could plausibly succeed. */
  get isRetryable(): boolean {
    return this.status >= 500 || this.status === 408 || this.status === 429;
  }

  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** A duplicate submission is a success, not an error — see the backend's
   *  idempotency contract. It should never reach here, but if the server ever
   *  changes its mind, the client should not treat it as a failure. */
  get isDuplicate(): boolean {
    return this.code === "IDEMPOTENCY_KEY_REUSED";
  }
}

/** The network did not answer. Distinct from a server that answered with an error. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    // `cause` is a standard Error property, so it is passed through rather than
    // shadowed — that keeps it visible in a devtools stack trace and in Sentry.
    super("The server could not be reached.", { cause });
    this.name = "NetworkError";
  }
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /**
   * A file upload. Sent as-is with **no** Content-Type header — the browser must
   * set it itself, because only the browser knows the multipart boundary it
   * generated. Setting `Content-Type: multipart/form-data` by hand is the classic
   * way to make a working upload return 400 with no useful message.
   */
  formData?: FormData;
  /**
   * `"blob"` for the spreadsheet downloads: the import template and the check
   * report are XLSX workbooks, and running them through `response.json()` would
   * corrupt them into an exception.
   */
  responseType?: "json" | "blob";
  /** Present on writes that must not duplicate. Mirrors the outbox's stored key. */
  idempotencyKey?: string;
  /** Shared across every retry of one logical attempt, so traces correlate. */
  requestId?: string;
  timeoutMs?: number;
  /**
   * Set on the handful of requests authenticated by the **device** token rather
   * than by the signed-in user — the heartbeat, and PIN sign-in.
   *
   * A 401 on one of those says nothing about the user's session, and treating it
   * as if it did signs somebody out for a reason that has nothing to do with
   * them. A terminal whose pairing was revoked would log its cashier out once a
   * minute, forever, with no explanation.
   */
  authenticatedByDevice?: boolean;
}

type TokenProvider = () => string | null;
type UnauthorizedHandler = () => void;
type HeaderProvider = () => Record<string, string>;

let accessTokenProvider: TokenProvider = () => null;
let refreshTokenProvider: TokenProvider = () => null;
let onTokensRefreshed: (access: string, refresh: string) => void = () => {};
let onUnauthorized: UnauthorizedHandler = () => {};
let scopeHeaderProvider: HeaderProvider = () => ({});

/** Wired up once at start-up by `connectAuthToApiClient`. */
export function configureAuth(options: {
  getAccessToken?: TokenProvider;
  getRefreshToken?: TokenProvider;
  onTokensRefreshed?: (access: string, refresh: string) => void;
  onUnauthorized?: UnauthorizedHandler;
  getScopeHeaders?: HeaderProvider;
}): void {
  if (options.getAccessToken) accessTokenProvider = options.getAccessToken;
  if (options.getRefreshToken) refreshTokenProvider = options.getRefreshToken;
  if (options.onTokensRefreshed) onTokensRefreshed = options.onTokensRefreshed;
  if (options.onUnauthorized) onUnauthorized = options.onUnauthorized;
  if (options.getScopeHeaders) scopeHeaderProvider = options.getScopeHeaders;
}

/**
 * In-flight refresh, shared by every caller.
 *
 * Without this, a screen that fires six queries on load would, on an expired
 * token, start six refreshes. With rotation enabled server-side the first
 * invalidates the token the other five are using, so five of them fail and the
 * user is signed out for no reason. One promise, awaited by all.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    const refresh = refreshTokenProvider();
    if (!refresh) return false;

    try {
      const response = await fetch(`${BASE_URL}/auth/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!response.ok) return false;

      const body = (await response.json()) as { access: string; refresh?: string };
      onTokensRefreshed(body.access, body.refresh ?? refresh);
      return true;
    } catch {
      // A network failure is not an authentication failure. Returning false
      // surfaces the original 401 rather than signing the user out for being
      // briefly offline.
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Routes that *are* the credentials: a 401 from one is a wrong PIN or a dead
 * refresh token, and refreshing to retry it would be meaningless or recursive.
 *
 * Listed rather than "anything under /auth/", which is what this used to be —
 * and which included `/auth/me/`, the call every page load makes to confirm the
 * stored session. Its access token has usually expired by the time somebody
 * reopens the app, so without a refresh the 401 went straight to sign-out:
 * everybody who came back more than fifteen minutes later was signed out.
 */
const CREDENTIAL_PATHS = [
  "/auth/login/",
  "/auth/refresh/",
  "/auth/logout/",
  "/auth/pin/",
  "/auth/phone/",
  "/auth/password/set/",
  "/auth/terminal/",
];

function isCredentialPath(path: string): boolean {
  return CREDENTIAL_PATHS.some((prefix) => path.startsWith(prefix));
}

const BASE_URL: string = import.meta.env.VITE_API_URL ?? "/api/v1";
const DEFAULT_TIMEOUT_MS = 15_000;
// An import upload is parsed and validated inside the request, so it takes
// meaningfully longer than a normal call. Fifteen seconds would abort a
// perfectly healthy 800-row file.
const UPLOAD_TIMEOUT_MS = 60_000;

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return attempt<T>(path, options, true);
}

async function attempt<T>(
  path: string,
  options: RequestOptions,
  mayRefresh: boolean,
): Promise<T> {
  const {
    body,
    formData,
    responseType = "json",
    idempotencyKey,
    authenticatedByDevice = false,
    requestId,
    timeoutMs = formData ? UPLOAD_TIMEOUT_MS : DEFAULT_TIMEOUT_MS,
    headers,
    ...rest
  } = options;

  const url = path.startsWith("http") ? path : `${BASE_URL}${path}`;

  const finalHeaders = new Headers(headers);
  finalHeaders.set("Accept", responseType === "blob" ? "*/*" : "application/json");
  if (body !== undefined) finalHeaders.set("Content-Type", "application/json");

  const token = accessTokenProvider();
  if (token) finalHeaders.set("Authorization", `Bearer ${token}`);

  // Branch and section narrowing. The server validates these against the user's
  // entitlements and ignores anything out of scope — they can narrow, never widen.
  for (const [name, value] of Object.entries(scopeHeaderProvider())) {
    finalHeaders.set(name, value);
  }

  if (idempotencyKey) finalHeaders.set("Idempotency-Key", idempotencyKey);
  // Echoed on the response and stamped into every server log line, so one
  // identifier follows a sale through three retries and two services.
  finalHeaders.set("X-Request-ID", requestId ?? crypto.randomUUID().replace(/-/g, ""));

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(url, {
      ...rest,
      headers: finalHeaders,
      signal: rest.signal ?? controller.signal,
      ...(formData !== undefined
        ? { body: formData }
        : body !== undefined
          ? { body: JSON.stringify(body) }
          : {}),
    });
  } catch (cause) {
    // Reaching here means we do not know whether the server acted. For a write,
    // that is precisely why the outbox keeps the operation and retries with the
    // same idempotency key rather than assuming failure.
    throw new NetworkError(cause);
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 204) return undefined as T;

  // A failed blob request still returns a JSON problem document, so the error
  // path below must read it as JSON regardless of what the caller asked for.
  if (responseType === "blob" && response.ok) {
    return (await response.blob()) as T;
  }

  const payload = await safeJson(response);

  if (!response.ok) {
    // A 401 usually means the fifteen-minute access token simply expired. Try a
    // refresh once, then replay. `mayRefresh` stops a failed refresh recursing.
    // A device-authenticated request is exempt from all of this: its 401 is
    // about the terminal's pairing, and the caller deals with it.
    if (response.status === 401 && !authenticatedByDevice) {
      // Usually the fifteen-minute access token simply expired. Try a refresh
      // once, then replay. `mayRefresh` stops a failed refresh recursing.
      if (mayRefresh && !isCredentialPath(path)) {
        if (await refreshAccessToken()) {
          return attempt<T>(path, options, false);
        }
      }
      onUnauthorized();
    }

    throw new ApiError(
      response.status,
      payload ?? {},
      response.headers.get("X-Request-ID") ?? undefined,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "PUT", body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "DELETE" }),

  /** Multipart upload. See `RequestOptions.formData` for why no Content-Type. */
  upload: <T>(path: string, formData: FormData, options?: RequestOptions) =>
    request<T>(path, { ...options, method: "POST", formData }),

  /** Fetches a file — the XLSX template and check report. */
  download: (path: string, options?: RequestOptions) =>
    request<Blob>(path, { ...options, method: "GET", responseType: "blob" }),
};

/**
 * Parse a JSON body, tolerating an absent or malformed one.
 *
 * Typed as `Partial<ApiErrorBody> | null` rather than `unknown` because both call
 * sites need it that way: the error path builds an ApiError from it, and the
 * success path casts to the endpoint's own type. Returning `unknown` only pushed
 * an unchecked assertion up to each caller.
 */
async function safeJson(response: Response): Promise<Partial<ApiErrorBody> | null> {
  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("application/json")) return null;
  try {
    return (await response.json()) as Partial<ApiErrorBody>;
  } catch {
    // A truncated or malformed body should surface as the HTTP failure it
    // accompanies, not as a JSON parse error further up the stack.
    return null;
  }
}
